import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { Button } from "@stockline/ui/components/button";
import { XCircle, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { PreOrder, ExportMetadata } from "@/app/api/types/preOrder";

interface ExportErrorPopoverProps {
  failedOrders: PreOrder[];
  onRetry: (orders: PreOrder[]) => void;
  isRetrying: boolean;
  children: React.ReactNode;
}

// Maps V2-side `last_error` codes to translation keys (fr/en/es). Anything
// outside this map falls through to the free-form `last_error_metadata.message`
// or legacy `error`, and finally to `common.unknownError`.
const ERROR_CODE_I18N: Record<string, string> = {
  ERP_DIVERGED: "ordersRecap.exportFailedErpDiverged",
  PO_REQUIRED_PROHIBITED: "ordersRecap.exportFailedPoRequiredProhibited",
  PO_REQUIRED_WARN: "ordersRecap.exportFailedPoRequiredWarn",
  ITDS_TOO_LONG: "ordersRecap.exportFailedItdsTooLong",
};

function resolveErrorMessage(
  exp: ExportMetadata | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (!exp) return t("common.unknownError");
  const code = exp.last_error;
  if (code && ERROR_CODE_I18N[code]) {
    // `last_error_metadata` carries VALUES only (counts, a product name) —
    // the sentence they slot into lives in `lib/locales/`. A worker never
    // ships copy, so a code whose message needs no params interpolates
    // nothing and is unaffected.
    // `product` is the one param a worker can legitimately send empty (a line
    // with neither name nor code), and the fallback WORD is copy — so it is
    // resolved here too, not backend-side.
    const params: Record<string, unknown> = { ...exp.last_error_metadata };
    if (!params.product) params.product = t("common.unknown");
    return t(ERROR_CODE_I18N[code], params);
  }
  // Free-form fallback chain: worker's structured message > legacy `error`
  // string > legacy `error_message` field > translated "unknown".
  return (
    exp.last_error_metadata?.message ||
    exp.error ||
    code ||
    t("common.unknownError")
  );
}

export function ExportErrorPopover({
  failedOrders,
  onRetry,
  isRetrying,
  children,
}: ExportErrorPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-80 p-3"
        align="start"
        data-testid="recap-export-error-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-2">
          <XCircle className="h-4 w-4 text-red-500 shrink-0" />
          <span className="text-sm font-semibold text-red-700">
            {t("ordersRecap.exportFailed")}
          </span>
        </div>

        <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
          {failedOrders.map((po) => (
            <div
              key={po.id}
              className="text-xs bg-red-50 rounded px-2 py-1.5 border border-red-100"
              data-testid="recap-export-error-row"
              data-preorder-id={po.id}
            >
              <span className="font-medium text-red-800">
                {po.partner?.name || po.id}
              </span>
              {/* The backend assembles the primary error + any advisory hints
                  into one message (newline-separated); render it verbatim with
                  preserved line breaks. */}
              <p
                className="text-red-600 mt-0.5 whitespace-pre-line"
                data-testid="recap-export-error-message"
              >
                {resolveErrorMessage(po.pre_order_metadata?.export, t)}
              </p>
            </div>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
          data-testid="recap-export-error-retry-button"
          onClick={(e) => {
            e.stopPropagation();
            onRetry(failedOrders);
            setOpen(false);
          }}
          disabled={isRetrying}
        >
          <RotateCcw className="h-3 w-3 mr-1.5" />
          {t("ordersRecap.retryExport")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
